import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { ItemCategoryReq, ItemRequest, ItemSubCategoryRequest, SizeRequest } from '@gtpl/shared-models/masters';
import { AllGrnReturnResponse, GrnItemReturnResponse, GrnItemReturnResponseModel, GrnItemsForReturn, GrnReturnGridDto } from '@gtpl/shared-models/procurement-management';
import { ItemCategoryService, ItemsService, ItemSubCategoryService, SizesService, VendorsService } from '@gtpl/shared-services/masters';
import { LogsService } from '@gtpl/shared-services/user-logs';
import { Injectable } from '@nestjs/common';
import { GenericTransactionManager } from 'apps/services/backend/typeorm-transactions/generic-transaction-manager';
import { LogsDto } from 'libs/shared-models/user-logs/src/lib/Logs/logs.dto';
import { GrnItems } from '../grn/grn-items.entity';
import { StockRepository } from '../grn/repository/stock-repository';
import { UnitRequest } from '../stock/dto/unit.id.request';
import { GrnDetailsForReturn } from './dto/grn-detail-for-return.dto';
import { GrnReturnIdRequest } from './dto/grn-return-id.request';
import { GrnReturnItemSaveDto } from './dto/grn-return-save.dto';
import { GrnReturnAdapter } from './grn-return-adapter';
import { GrnReturns } from './grn-return-entity';
import { GrnReturnRepository } from './repository/grn-return-repository';

@Injectable()
export class GrnReturnsService {
    constructor(
      private logService:LogsService,

        private readonly adapter: GrnReturnAdapter,
        private readonly grnReturnsRep: GrnReturnRepository,
        private itemCategoryService: ItemCategoryService,
        private itemService: ItemsService,
        private subCategoryService: ItemSubCategoryService,
        private vendorService: VendorsService,
        private sizeService: SizesService,
    ) {
    }

    async saveGrnReturns(grnReturnDto: GrnDetailsForReturn): Promise<GrnItemReturnResponseModel> {
        const manager = new GenericTransactionManager();
        try {
            const today = new Date();
            const CurrentYear = today.getFullYear();
            let totalGrnRtn = await this.grnReturnsRep.count({where:{plantId:grnReturnDto.plantId}});

            totalGrnRtn = totalGrnRtn + 1;

            let refNo = totalGrnRtn + "";
            while (refNo.length < 4) refNo = "0" + refNo;
            const vendorCode = grnReturnDto.vendorName.match(/\b(\w)/g).join(''); // get starting letter of a words in a string
            const grnRtnNumber = "GRNRTN/" + (CurrentYear.toString().substr(-2)) + "/" + vendorCode + "/" + refNo;
            grnReturnDto.grnReturnNumber = grnRtnNumber;


            await manager.startTransaction();
            const savedRecords = await manager.getCustomRepository(GrnReturnRepository).save(this.adapter.convertDtoToEntity(grnReturnDto));
            if (savedRecords) {
                for (const item of savedRecords.grnReturnItemsInfo) {
                    // check stock available qty
                    const available = await manager.getCustomRepository(StockRepository).findOne({ where: { stockId: item.stockId } });
                    if (available.physicalQuantity >= item.grnReturnQty) {
                        // update grn return items
                        const updateGrnRtn = await manager.getRepository(GrnItems).update({ grnItemId: item.grnItemId }, { returnedQuantity: () => `returned_quantity+${item.grnReturnQty}` });
                        if (!updateGrnRtn.affected) {
                            throw new ErrorResponse(9999, 'Failed To Update Grn Return Qty')
                        }
                        // update stock
                        const updateStock = await manager.getCustomRepository(StockRepository).update({ stockId: item.stockId }, { physicalQuantity: () => `physical_quantity-${item.grnReturnQty}` });

                        if (!updateStock.affected) {
                            throw new ErrorResponse(9999, 'Failed To Update Stock')
                        }
                    } else {
                        throw new ErrorResponse(9999, 'Something Went Wrong, Please check Stock Again');
                    }

                }
                manager.completeTransaction();
                return new GrnItemReturnResponseModel(true, 123, 'Saved successfully');
            } else {
                throw new ErrorResponse(9999, 'Failed To Create');
            }
        } catch (error) {
            await manager.releaseTransaction();
            return error
        }
    }

    async getAllGrnReturns(req: UnitRequest): Promise<AllGrnReturnResponse> {
        try {
            const grnRtnData: GrnReturnGridDto[] = await this.grnReturnsRep.createQueryBuilder('grn_return')
                .select('grn_return_id as grnReturnId, grn_return_date as grnReturnDate, grn_return_number as grnReturnNumber, grn.grn_no as grnNumber, grn.grn_id as grnId, grn.grn_date as grnDate, grn_return.remarks')
                .leftJoin('grn_return.grnId', 'grn')
                .where(`plant_id = ` + req.unitId)
                .orderBy(`status`, "ASC")
                .getRawMany();
            if (grnRtnData && grnRtnData.length) {
                const resp=  new AllGrnReturnResponse(true, 1111, 'Grn Return Data Found', grnRtnData);
                const newLogDto = new LogsDto(1,'view grn returns', 'GrnReturns', 0, true, resp.internalMessage,'')
                let res = await this.logService.createLog(newLogDto);
                console.log(res);
                return resp
            } else {
                return new AllGrnReturnResponse(false, 9999, 'No Data Found');
            }
        } catch (error) {
            return error;
        }
    }


    async getGrnReturnDataById(req: GrnReturnIdRequest): Promise<GrnItemReturnResponse> {
        try {
            const grnRtnData: GrnReturns = await this.grnReturnsRep.createQueryBuilder('grn_return')
                .leftJoinAndSelect('grn_return.grnId', 'grnId')
                .leftJoinAndSelect('grn_return.grnReturnItemsInfo', 'grnReturnItemsInfo')
                .where(`grn_return.grn_return_id = ${req.grnReturnId}`)
                .orderBy(`status`, "ASC")
                .getOne();
            if (grnRtnData) {
                const grnReturnData = new GrnDetailsForReturn();
                grnReturnData.grnDate = grnRtnData.grnId.grnDate
                grnReturnData.grnId = grnRtnData.grnId.grnId;
                grnReturnData.grnNumber = grnRtnData.grnId.grnNumber;
                grnReturnData.invoiceDate = grnRtnData.grnId.InvoiceDate;
                grnReturnData.invoiceNumber = grnRtnData.grnId.invoiceNumber;
                grnReturnData.vendorId = grnRtnData.grnId.vendorId;
                grnReturnData.remarks = grnRtnData.remarks;
                const vendorDetails = await this.vendorService.getVendorDataById({ vendorId: grnRtnData.grnId.vendorId });
                grnReturnData.vendorName = vendorDetails.data.vendorName;
                const items: GrnItemsForReturn[] = [];
                for (const grnItem of grnRtnData.grnReturnItemsInfo) {
                    const item = new GrnItemsForReturn();
                    // get item Category, item Sub Category, item, 
                    const itemCategoryReq = new ItemCategoryReq(grnItem.itemCategoryId);
                    const itemCategoryDetails = this.itemCategoryService.getItemCategoryById(itemCategoryReq);
                    if (!itemCategoryDetails) {
                        throw new ErrorResponse(1111, 'Failed to Get Item Category');
                    }

                    // get item details for item id 
                    const itemReq = new ItemRequest(grnItem.itemId);
                    const itemDetails = this.itemService.getItemById(itemReq);
                    if (!itemDetails) {
                        throw new ErrorResponse(1111, 'Failed to Get Item');
                    }

                    // const size = new SizeRequest(grnItem.sizeId);
                    // const sizeDetails = this.sizeService.getSizeDataById(size);
                    // if (!size) {
                    //     throw new ErrorResponse(1111, 'Failed to Get Size');
                    // }

                    // get item sub category details for item sub category id
                    const itemSubCategoryReq = new ItemSubCategoryRequest(grnItem.itemSubCategoryId);
                    const itemSubCategoryDetails = this.subCategoryService.getItemSubCategoryForId(itemSubCategoryReq);
                    if (!itemSubCategoryDetails) {
                        throw new ErrorResponse(1111, 'Failed to Get Item Category');
                    }


                    await Promise.all([itemCategoryDetails, itemDetails, itemSubCategoryDetails]).then(res => {
                        const itemCategory = (res[0]) ? res[0] : null;
                        const itemSubCategory = (res[2]) ? res[2] : null;
                        const itemData = (res[1]) ? res[1] : null;
                        // const size = (res[2]) ? res[2] : null;

                        item.itemCategoryId = itemCategory.itemCategoryId;
                        item.itemCategoryName = itemCategory.itemCategory;
                        item.itemSubCategoryId = itemSubCategory.itemSubCategoryId;
                        item.itemSubCategoryName = itemSubCategory.itemSubCategory;
                        item.itemId = itemData.itemId;
                        item.itemName = itemData.itemName;
                        // item.sizeId = size.sizeId;
                        // item.sizeName = size.sizeName;
                        //discount
                        item.discountPercentage = Number(grnItem.discountPercentage);
                        item.discountAmount = Number(grnItem.discountAmount);

                        //tax
                        item.taxPercentage = Number(grnItem.taxPercentage);
                        item.taxAmount = Number(grnItem.taxAmount);

                        item.grnItemId = grnItem.grnItemId;
                        item.stockId = grnItem.stockId;
                        item.grnReceivedQty = Number(grnItem.grnReceiveQty);
                        item.grnReturnQty = Number(grnItem.grnReturnQty);
                        item.availQty = Number(grnItem.availableQty);
                        item.unitPrice = Number(grnItem.unitPrice);
                        item.grnTotalReturnPrice = Number(grnItem.grnTotalReturnPrice);
                        items.push(item);
                    })
                }
                grnReturnData.grnItems = items;
                return new GrnItemReturnResponse(true, 1111, 'Grn Return Data Found', grnReturnData);
            } else {
                return new GrnItemReturnResponse(false, 9999, 'No Data Found');
            }
        } catch (error) {
            return error;
        }
    }
}
